import { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { listRtoRecords, getRtoPack, saveRtoFeedback } from '../api/rtoRecords';
import { syncNew } from '../api/orderHistory';

const PRESETS = [
  { key: 'today',    label: 'Today' },
  { key: '7days',    label: 'Last 7 days' },
  { key: 'week',     label: 'This week' },
  { key: 'month',    label: 'This month' },
  { key: 'lifetime', label: 'Lifetime' },
  { key: 'custom',   label: 'Custom' },
];
const PAGE = 15;

function computeDateRange(k, cFrom, cTo) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(); end.setHours(23, 59, 59, 999);
  if (k === 'today') return { from: today, to: end };
  if (k === '7days') { const f = new Date(today); f.setDate(f.getDate() - 6); return { from: f, to: end }; }
  if (k === 'week')  { const f = new Date(today); const d = f.getDay(); f.setDate(f.getDate() - (d === 0 ? 6 : d - 1)); return { from: f, to: end }; }
  if (k === 'month') return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: end };
  if (k === 'lifetime') return { from: null, to: null };
  if (k === 'custom') return { from: cFrom ? new Date(cFrom + 'T00:00:00') : null, to: cTo ? new Date(cTo + 'T23:59:59') : null };
  return { from: null, to: null };
}
function fmtYMD(d) {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtINR(a) { return a == null ? '—' : '₹' + Number(a).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
const YN = (v) => (v ? <span className="text-zoddle-pink font-bold">Y</span> : <span className="text-gray-300">N</span>);

// ── See-pack modal ──────────────────────────────────────────────────────────
function PackModal({ uid, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getRtoPack(uid).then(r => setItems(r.items || [])).catch(() => setItems([]));
  }, [uid]);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-800">Dispatched pack · {uid}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        {items == null ? <div className="text-gray-400 py-8 text-center">Loading…</div>
          : items.length === 0 ? <div className="text-gray-400 py-8 text-center">No dispatched items recorded.</div>
          : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {items.map((it, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-2 text-center">
                {it.image_url_1
                  ? <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-full aspect-square object-cover rounded mb-1" />
                  : <div className="w-full aspect-square bg-gray-100 rounded mb-1" />}
                <div className="text-[11px] font-semibold text-gray-700 truncate">{it.brand_name || '—'}</div>
                <div className="text-[10px] text-gray-500 truncate">{it.zoddle_barcode} · {it.size || ''}</div>
                <div className="text-[11px] text-zoddle-green font-bold">{fmtINR(it.price)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Edit modal ──────────────────────────────────────────────────────────────
function EditModal({ record, onClose, onSaved }) {
  const [price, setPrice]   = useState(record.price_issue);
  const [design, setDesign] = useState(record.design_issue);
  const [size, setSize]     = useState(record.size_issue);
  const [comments, setComments] = useState(record.comments || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await saveRtoFeedback(record.appointment_uid, {
        price_issue: price, design_issue: design, size_issue: size,
        comments: comments.trim() || null,
      });
      onSaved({ ...record, price_issue: price, design_issue: design, size_issue: size, comments: comments.trim() || null });
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  const Check = ({ label, val, set }) => (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input type="checkbox" checked={val} onChange={e => set(e.target.checked)} className="w-4 h-4 accent-zoddle-pink" />
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-gray-800 mb-1">Why did nothing get bought?</h3>
        <p className="text-xs text-gray-500 mb-4">{record.customer_name || '—'} · {record.appointment_uid}</p>
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-500 mb-2">Cause(s) — select any</div>
          <div className="flex flex-col gap-2">
            <Check label="Price issue" val={price} set={setPrice} />
            <Check label="Design issue" val={design} set={setDesign} />
            <Check label="Size issue" val={size} set={setSize} />
          </div>
        </div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">Comments</label>
        <textarea value={comments} onChange={e => setComments(e.target.value)} rows={4}
          className="w-full border border-gray-200 rounded-md p-2 text-sm focus:outline-none focus:ring-1 focus:ring-zoddle-green"
          placeholder="Explain why nothing was bought…" />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-zoddle-green rounded-md hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function RTORecords() {
  const [preset, setPreset] = useState('7days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [packUid, setPackUid] = useState(null);
  const [editRec, setEditRec] = useState(null);

  const filters = useCallback((offset) => {
    const { from, to } = computeDateRange(preset, customFrom, customTo);
    return { date_from: fmtYMD(from), date_to: fmtYMD(to), offset, limit: PAGE };
  }, [preset, customFrom, customTo]);

  const load = useCallback(async (offset = 0, append = false) => {
    setLoading(true);
    try {
      const r = await listRtoRecords(filters(offset));
      setTotal(r.total);
      setRecords(prev => append ? [...prev, ...r.records] : r.records);
    } catch (e) {
      if (!append) setRecords([]);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(0, false); }, [load]);

  async function doSync() {
    setSyncing(true);
    try { await syncNew(); await load(0, false); }
    catch (e) { alert('Sync failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSyncing(false); }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">RTO Records</h2>
          <p className="text-sm text-gray-500">Customer saw the pack and bought nothing — record the cause.</p>
        </div>
        <button onClick={doSync} disabled={syncing}
          className="px-4 py-2 text-sm font-semibold text-zoddle-green border border-zoddle-green rounded-md hover:bg-green-50 disabled:opacity-50">
          {syncing ? 'Syncing…' : 'Sync orders'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 text-sm rounded-full border ${preset === p.key
              ? 'bg-zoddle-green text-white border-zoddle-green'
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <span className="flex items-center gap-1">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="border border-gray-200 rounded-md px-2 py-1 text-sm" />
            <span className="text-gray-400">–</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="border border-gray-200 rounded-md px-2 py-1 text-sm" />
          </span>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
              <th className="px-3 py-2 font-semibold">Date</th>
              <th className="px-3 py-2 font-semibold">Customer (Mobile)</th>
              <th className="px-3 py-2 font-semibold">Executive</th>
              <th className="px-3 py-2 font-semibold">Items dispatched</th>
              <th className="px-3 py-2 font-semibold text-center">Price</th>
              <th className="px-3 py-2 font-semibold text-center">Design</th>
              <th className="px-3 py-2 font-semibold text-center">Size</th>
              <th className="px-3 py-2 font-semibold">Comments</th>
              <th className="px-3 py-2 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {records.map(r => (
              <tr key={r.appointment_uid} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{r.customer_name || '—'}</div>
                  <div className="text-xs text-gray-400">{r.customer_phone || ''}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{r.executive || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className="font-medium">{r.items_dispatched}</span>
                  {r.pack_rating != null && <span className="text-xs text-gray-400"> · ★{r.pack_rating}</span>}
                  {r.has_pack && (
                    <button onClick={() => setPackUid(r.appointment_uid)}
                      className="ml-2 text-xs text-zoddle-blue font-semibold hover:underline">See pack</button>
                  )}
                </td>
                <td className="px-3 py-2 text-center">{YN(r.price_issue)}</td>
                <td className="px-3 py-2 text-center">{YN(r.design_issue)}</td>
                <td className="px-3 py-2 text-center">{YN(r.size_issue)}</td>
                <td className="px-3 py-2 max-w-xs"><div className="truncate text-gray-600" title={r.comments || ''}>{r.comments || '—'}</div></td>
                <td className="px-3 py-2">
                  <button onClick={() => setEditRec(r)}
                    className="text-xs font-semibold text-zoddle-green hover:underline">Edit</button>
                </td>
              </tr>
            ))}
            {!loading && records.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">No RTOs in this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-gray-400">Showing {records.length} of {total}</span>
        {records.length < total && (
          <button onClick={() => load(records.length, true)} disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-md hover:border-gray-300 disabled:opacity-50">
            {loading ? 'Loading…' : `Load ${Math.min(PAGE, total - records.length)} more`}
          </button>
        )}
      </div>

      {packUid && <PackModal uid={packUid} onClose={() => setPackUid(null)} />}
      {editRec && (
        <EditModal record={editRec} onClose={() => setEditRec(null)}
          onSaved={(upd) => { setRecords(rs => rs.map(x => x.appointment_uid === upd.appointment_uid ? upd : x)); setEditRec(null); }} />
      )}
    </div>
  );
}
